import {
  cpi,
  eac,
  etc,
  runRate,
  spi,
  tcpi,
  vac,
  workdaysBetween,
  type ContractEffort,
  type EacMode,
  type EffortPeriod,
} from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C65 · every derived figure on the project Dashboard, computed once.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME CONTRACT AS `finance-model.ts`, AND FOR THE SAME REASON.** Six tiles, four bars,
 * a run-rate footer and a seven-row list panel all quote the frame, the rate and what is left of
 * both. Derived per component they drift, and the way that shows up is a tile disagreeing with the
 * bar beneath it.
 *
 * ⚠️⚠️ **THE SCREEN HAS THREE SHAPES AND THE MOCKUP DRAWS ALL THREE.** A retainer with one period
 * picked asks *"is this month going to fit"*; the same retainer whole asks *"is the series
 * holding"*; a one-off project asks *"will it land on time and inside the estimate"*. `variant`
 * says which, and every panel switches on it rather than inferring it from a figure being zero.
 *
 * ⚠️ **Everything here is arithmetic over the wire's primitives — with three exceptions.**
 * `burnOutOn`, `forecastFinishOn` and `slipWorkdays` are counted in the workspace's WORKING days
 * and arrive computed; `effort.ts` says why they cannot be worked out in a browser.
 */

export type EffortVariant = 'occurrence' | 'series' | 'plain';

export interface EffortModel {
  readonly variant: EffortVariant;

  /** The frame for the scope, what was planned into it, and what came out of it. */
  readonly budget: number;
  readonly budgetTotal: number;
  readonly logged: number;
  readonly planned: number;
  readonly plannedToDate: number;
  readonly billable: number;
  readonly overtime: number;
  /** ⚠️ Frame − logged. Negative is an overrun, and the row says so rather than clamping. */
  readonly remaining: number;
  readonly usedShare: number | null;

  readonly workdays: number;
  readonly workdaysDone: number;
  readonly workdaysLeft: number;
  readonly elapsedShare: number;

  /** Hours a day: what is being spent, what the frame allows, and the gap. */
  readonly rate: number;
  readonly allowedRate: number;
  readonly rateDelta: number;
  /** `RATE × the scope's working days` — where the period lands if the pace holds. */
  readonly projected: number;
  readonly overrun: number;
  readonly burnOutOn: string | null;

  /** Σ estimate and Σ earned across the scope — progress, which consumption is not. */
  readonly estimate: number;
  readonly earned: number;
  readonly doneShare: number | null;
  readonly spentShare: number | null;

  readonly cpi: number | null;
  readonly spi: number | null;
  readonly eac: number | null;
  readonly etc: number | null;
  readonly vac: number | null;
  readonly tcpi: number | null;
  /** EV − AC in hours: negative means more hours went in than work came out. */
  readonly costVariance: number;
  /** EV − PV in hours: negative means less is done than the plan said would be. */
  readonly scheduleVariance: number;

  /** The series panel — every figure `SOROZAT` draws. */
  readonly closedPeriods: number;
  readonly overspentPeriods: number;
  readonly periodBudget: number;
  readonly averageUsage: number;
  readonly averageBudget: number;
  readonly carryOver: number;
  readonly periodsLeft: number;
  readonly closedBudget: number;
  readonly nextPeriod: EffortPeriod | null;
  readonly nextPlan: number | null;
  readonly nextPlanVariance: number | null;

  /** The schedule panel — every figure `ÜTEM` draws. */
  readonly deadline: string;
  readonly forecastFinishOn: string | null;
  readonly slipWorkdays: number | null;
  readonly slipDays: number | null;
  readonly lateTasks: number;
  /** Σ estimate × (1 − weight) on tasks whose due date has passed: work already slipping. */
  readonly lateHours: number;
  /** Mean working-day age of the open tasks that have started. ⚠️ Rising means a pile-up. */
  readonly openTaskAge: number;
}

export function deriveEffort(
  view: ContractEffort,
  options: { readonly eacMode: EacMode },
): EffortModel {
  const n = (value: string): number => Number(value);

  const budget = n(view.budget);
  const logged = n(view.logged);
  const plannedToDate = n(view.plannedToDate);
  const estimate = n(view.estimate);
  const earned = n(view.earned);

  const workdays = view.workdays || 1;
  const workdaysDone = Math.min(view.workdaysDone, workdays);

  const rate = runRate(logged, workdaysDone);
  const allowedRate = budget / workdays;
  /* 30 Oct 2026 · EV · the server evaluated all five; this reads the one on screen. Alaprátás's
     formula where an older read carries no evaluation. */
  const chosen = view.eac?.modes.find((one) => one.mode === options.eacMode) ?? null;
  const estimateAtCompletion =
    chosen === null
      ? eac('etc', budget, logged, earned)
      : chosen.value === null
        ? null
        : Number(chosen.value);
  const finishOn = chosen === null ? view.forecastFinishOn : chosen.finishOn;
  const slipWorkdays = chosen === null ? view.slipWorkdays : chosen.slipWorkdays;

  const closed = view.periods.filter((period) => period.state === 'past');
  const closedUsage = closed.reduce((total, period) => total + Number(period.logged), 0);
  const closedBudget = closed.reduce((total, period) => total + Number(period.budget), 0);

  /* ⚠️ The NEXT period is the first one not yet begun — never `current + 1`, which on a contract
     whose periods have all closed points at a period that does not exist. */
  const nextPeriod = view.periods.find((period) => period.state === 'future') ?? null;
  const nextPlan = nextPeriod === null ? null : Number(nextPeriod.planned);
  const nextBudget = nextPeriod === null ? 0 : Number(nextPeriod.budget);

  const open = view.tasks.filter((task) => task.statusCategory !== 'closed');
  const late = open.filter((task) => task.dueOn !== null && task.dueOn < view.today);
  const started = open.filter((task) => task.startedOn !== null && task.startedOn <= view.today);

  return {
    variant: !view.recurring ? 'plain' : view.selectedPeriod === null ? 'series' : 'occurrence',

    budget,
    budgetTotal: n(view.budgetTotal),
    logged,
    planned: n(view.planned),
    plannedToDate,
    billable: n(view.billable),
    overtime: n(view.overtime),
    remaining: budget - logged,
    usedShare: budget > 0.004 ? logged / budget : null,

    workdays,
    workdaysDone,
    workdaysLeft: Math.max(workdays - workdaysDone, 0),
    elapsedShare: workdaysDone / workdays,

    rate,
    allowedRate,
    rateDelta: rate - allowedRate,
    projected: rate * workdays,
    overrun: rate * workdays - budget,
    burnOutOn: view.burnOutOn,

    estimate,
    earned,
    doneShare: budget > 0.004 ? earned / budget : null,
    spentShare: budget > 0.004 ? logged / budget : null,

    cpi: cpi(earned, logged),
    spi: spi(earned, plannedToDate, budget),
    eac: estimateAtCompletion,
    etc: etc(estimateAtCompletion, logged),
    vac: vac(budget, estimateAtCompletion),
    tcpi: tcpi(budget, earned, logged),
    costVariance: earned - logged,
    scheduleVariance: earned - plannedToDate,

    closedPeriods: closed.length,
    overspentPeriods: closed.filter(
      (period) => Number(period.logged) > Number(period.budget) + 0.004,
    ).length,
    periodBudget: view.periodCount > 0 ? n(view.budgetTotal) / view.periodCount : 0,
    averageUsage: closed.length > 0 ? closedUsage / closed.length : 0,
    averageBudget: closed.length > 0 ? closedBudget / closed.length : 0,
    /* ⚠️ Unused frame is a real number on a retainer — it is what the renewal conversation is
       about. Positive is frame left on the table; negative is an overrun already taken. */
    carryOver: closedBudget - closedUsage,
    /* ⚠️ Remaining means NOT CLOSED, so the running period counts. It is not over. */
    periodsLeft: view.periods.length - closed.length,
    closedBudget,
    nextPeriod,
    nextPlan,
    nextPlanVariance: nextPlan === null ? null : nextPlan - nextBudget,

    deadline: view.to,
    forecastFinishOn: finishOn,
    slipWorkdays,
    slipDays: finishOn === null ? null : calendarDays(view.to, finishOn),
    lateTasks: late.length,
    lateHours: late.reduce(
      /* EV-2 · the workspace's weight, as the server sent it with the task. */
      (total, task) => total + Number(task.estimate) * (1 - Number(task.weight)),
      0,
    ),
    openTaskAge:
      started.length === 0
        ? 0
        : Math.round(
            started.reduce(
              (total, task) => total + workdaysBetween(task.startedOn as string, view.today),
              0,
            ) / started.length,
          ),
  };
}

/** ⚠️ Calendar days, the one of the two slip units a browser CAN count. */
function calendarDays(from: string, to: string): number {
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86_400_000);
}
